import React from 'react';
import { Gauge } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { ProductivityData } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote } from '../components';
import { Card, Hero, MonoLabel, StageBand, PageHeader, Banner, VBars } from '../dashboards/ui';

const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—';

const MyProductivityPage: React.FC = () => {
  const { data, loading, error } = useAsync<ProductivityData>(() => aaiApi.getProductivity(), 'Could not load your productivity.');
  if (loading) return <Loading label="Loading your productivity…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  const bars = data.series.map((s) => ({ label: shortDate(s.measured_at), pct: Math.round((s.midpoint || 0) * 100) }));

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="My productivity"
        subtitle="Your AI growth over time — measured against your own pre-AI baseline."
      />

      <Banner tone="blue" icon={<Gauge size={16} className="text-brand-500 shrink-0" />}>
        Your numbers, vs. your own pre-AI baseline. Self-estimated and shown as ranges — never reported up as a precise figure.
      </Banner>

      <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-4 mb-4">
        <Card>
          <MonoLabel className="mb-1">Your readiness trend</MonoLabel>
          <p className="text-[12px] text-gray-400 mb-4">Each check-in, as a confidence-weighted midpoint.</p>
          {bars.length === 0 ? (
            <p className="text-[13px] text-gray-500">No measurements yet — complete onboarding to start your trend.</p>
          ) : (
            <VBars bars={bars} />
          )}
        </Card>

        <div className="space-y-4">
          <Hero>
            <MonoLabel tone="accent" className="mb-2">Time redirected</MonoLabel>
            <div className="text-3xl font-bold tracking-tight">{data.hours_per_week.low}–{data.hours_per_week.high}
              <span className="text-[15px] font-medium text-white/60"> {data.hours_per_week.unit}</span>
            </div>
            <p className="text-[12.5px] text-white/60 mt-1.5 leading-relaxed">Estimated time moved to higher-value work. A range — not a hard number.</p>
          </Hero>
          <Card>
            <MonoLabel className="mb-2">Confidence</MonoLabel>
            <div className="mb-1"><StageBand stage={data.current_stage} /></div>
            <p className="text-[12.5px] text-gray-500 mt-2 leading-relaxed">
              Self-reported, and discounted ~30% before it rolls into any team number. {data.improved ? "You've moved up since your first check-in." : 'Re-checked monthly — keep going to see your trend.'}
            </p>
          </Card>
        </div>
      </div>

      {data.series.length > 0 && (
        <Card>
          <MonoLabel className="mb-3">Check-in history</MonoLabel>
          <div className="space-y-2.5">
            {data.series.map((s, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-lg px-3.5 py-2.5">
                <span className="font-sans text-[11px] text-gray-400 w-20 shrink-0">{shortDate(s.measured_at)}</span>
                <StageBand stage={s.stage} />
                <div className="flex-1 h-1.5 rounded-full bg-gray-100"><div className="h-1.5 rounded-full bg-brand-500" style={{ width: `${Math.round((s.midpoint || 0) * 100)}%` }} /></div>
                <span className="text-[11px] tabular-nums text-gray-500 w-10 text-right shrink-0">{Math.round((s.midpoint || 0) * 100)}%</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default MyProductivityPage;
